import {
    INTERNATIONAL_GET_AVAILABLE_FARE_RULE,
    INTERNATIONAL_GET_POLICIES,
    INTERNATIONAL_GET_AIRPORTS,
    INTERNATIONAL_GET_MORE_INFO,
    INTERNATIONAL_GET_ORDER_DETAILS,
} from '~endpoints';
import { mapMutations, mapState, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
// errors
import { internationalErrors } from '~/scripts/utils/errors';

export default {
    data () {
        return {
            oldPrice: 0,
            priceChanged: false,
        };
    },
    computed: {
        ...mapState('international', ['selectedPackage']),
    },
    methods: {
        ...mapMutations('international', ['setPackage', 'setFareRule', 'setPolicies', 'setInsuranceInfo', 'setConfirmInfo', 'setLoadingMoreInfo']),
        ...mapActions(['openAlert']),
        getAirport (domainCode) {
            if (!domainCode) {
                return { then: () => {} };
            }
            const param = 'domainCode={"eq":"' + domainCode + '"}';
            return new Promise((resolve, reject) => {
                this.$axios.$get(INTERNATIONAL_GET_AIRPORTS,
                    {
                        params: {
                            filter: param,
                        },
                    }).then(response => {
                    const result = response.result.items[0];
                    resolve(result);
                }).catch(() => {

                });
            });
        },
        getCity: debounce(function (domainCode, type, data) {
            if (!domainCode) {
                return;
            }
            if (type === 'destination') {
                // Start the destination input spinner
                this.isDestinationSearchLoading = true;
            } else {
                // Start the origin input spinner
                this.isOriginSearchLoading = true;
            }
            // Query for asyncGetCities
            const param = 'q={"ct":"' + domainCode + '"}';
            this.$axios.$get(INTERNATIONAL_GET_AIRPORTS,
                {
                    params: {
                        filter: param,
                        page_size: 20,
                    },
                })
                .then(response => {
                    // Show the searched cities
                    data.citiesToShow = response.result.items.filter((airport) => {
                        return this.notDomestic(data, airport, type);
                    });
                }).finally(() => {
                    // Stop the spinner
                    if (type === 'destination') {
                        this.isDestinationSearchLoading = false;
                    } else {
                        data.isOriginSearchLoading = false;
                    }
                });
        }, 500),
        notDomestic (data, airport, type) {
            if (!airport.domainCode) {
                return false;
            }
            if (type === 'origin') {
                if (data.selectedDestination.city) {
                    // Can't select the same city or two cities from Iran
                    if (airport.domainCode !== data.selectedDestination.domainCode && airport.city.name !== data.selectedDestination.city.name) {
                        if (airport.city.country.name === 'Iran' && data.selectedDestination.city.country.name === 'Iran') {
                            return false;
                        }
                        return true;
                    }
                } else {
                    return true;
                }
            } else if (type === 'destination') {
                if (data.selectedOrigin.city) {
                    // Can't select the same city or two cities from Iran
                    if (airport.domainCode !== data.selectedOrigin.domainCode && airport.city.name !== data.selectedOrigin.city.name) {
                        if (airport.city.country.name === 'Iran' && data.selectedOrigin.city.country.name === 'Iran') {
                            return false;
                        }
                        return true;
                    }
                } else {
                    return true;
                }
            }
        },
        getPolicies (available) {
            if ((available.policies && available.policies.success) || available.policies.loading) {
                return;
            }
            this.setPolicies({ id: available.proposalId, result: { loading: true } });
            this.$axios.$get(INTERNATIONAL_GET_POLICIES, {
                params: {
                    proposalId: available.proposalId,
                },
            }).then(result => {
                this.setPolicies({ id: available.proposalId, result });
                available.policies = result;
            }).catch(() => {
                available.policies = { error: true, loading: false };
            });
        },
        async getAvailableFareRule (available) {
            if (!available.fareRule.result && !available.fareRule.loading) {
                try {
                    this.setFareRule({ id: available.proposalId, result: { loading: true } });
                    const result = await this.$axios.$get(
                        INTERNATIONAL_GET_AVAILABLE_FARE_RULE({ proposalId: available.proposalId })
                    );
                    this.setFareRule({ id: available.proposalId, result });
                } catch (e) {
                    this.setFareRule({ id: available.proposalId, result: { error: true, loading: false } });
                }
            }
        },
        getFailed (available) {
            this.$set(available, 'updateFailed', true);
            // wait fot update results to prevent reset filters (loadingMoreInfo checked in results watcher)
            this.$emit('set-available', available, available);
            this.setLoadingMoreInfo(false);
            this.$set(available, 'loadingMoreInfo', false);
            this.openAlert(internationalErrors.moreInfoFailed);
            this.$setTimeout(() => {
                this.cancelAvailable();
            }, 3000);
        },
        // update flight proposal data (price change , baggage)
        getMoreInfo (available, selected = false) {
            // if this method was called or is calling , return
            if ((available.loadingMoreInfo || available.moreInfoCalled) && this.$route.query.step !== 'confirm') {
                return;
            }
            this.setLoadingMoreInfo(true);
            this.$set(available, 'loadingMoreInfo', true);
            return this.$axios.$get(INTERNATIONAL_GET_MORE_INFO({ proposalId: available.proposalId }), {
                params: {
                    fetchType: this.$route.query.step === 'confirm' ? 1 : null,
                },
            }).then(async (response) => {
                // replace old proposal data with updated data
                this.$set(available, 'updateFailed', false);
                const newPackage = response.result;
                const temp = available;
                const oldTotal = available.total;
                temp.total = newPackage.total;
                temp.prices = newPackage.prices;
                if (this.isMultiDestination) {
                    temp.flightGroups = newPackage.flightGroups;
                } else {
                    temp.leavingFlightGroup = newPackage.leavingFlightGroup;
                    temp.returningFlightGroup = newPackage.returningFlightGroup;
                }
                temp.moreInfoCalled = true;
                temp.isRefundable = newPackage.isRefundable;
                if (selected) {
                    this.$emit('set-more-info', true);
                    this.setPackage(temp);
                } else {
                    // wait fot update results to prevent reset filters (loadingMoreInfo must be true during results watcher executing)
                    await this.$emit('set-available', available, temp);
                }
                this.setLoadingMoreInfo(false);
                this.$set(available, 'loadingMoreInfo', false);
                // alert if price is changed
                if (oldTotal !== temp.total) {
                    this.oldPrice = oldTotal;
                    this.priceChanged = true;
                }
            }, () => {
                this.getFailed(available);
            }).catch(() => {
                this.getFailed(available);
            });
        },
        getOrderDetails (orderId) {
            this.$axios.$get(INTERNATIONAL_GET_ORDER_DETAILS({ orderId: orderId }))
                .then((data) => {
                    // set confirm info in store
                    if (data.result.description) { this.openAlert(data.result.description); }
                    this.setConfirmInfo(...data.result.internationalFlightOrders);
                    this.setInsuranceInfo(...data.result.insuranceOrders);
                    // remove old info of order and user from universal storage (only when it exists)
                    if (this.$storage.getLocalStorage('internationalConfirmInfo')) {
                        this.$storage.removeUniversal('internationalConfirmInfo');
                    }
                    // remove old queryString of order and user from universal storage  (only when it exists)
                    if (this.$storage.getUniversal('queryString')) {
                        this.$storage.removeUniversal('queryString');
                    }
                    // remove old queryString of order and user from universal storage  (only when it exists)
                    this.$storage.setLocalStorage('internationalConfirmInfo', ...data.result.internationalFlightOrders);
                    // this.fetching = false;
                    if (this.$route.query.step === 'passengers') {
                        // send checkout step to GTM (step 1)
                        this._$if__sendCheckoutStep(1);
                        this.$router.push({
                            query: Object.assign({ }, this.$router.currentRoute.query, { step: 'confirm' }),
                        });
                    } else { // in confirm step
                        // price is changed, recalculate amount values
                        this.oldPrice = this.amount.pay;
                        this.initialize();
                        this.priceChanged = true;
                        this.getMoreInfo(this.selectedPackage, true);
                    }
                })
                .catch(error => {
                    this.$route.query.step === 'passengers' && this.sendPassengerErrorEvent(error.response.status);
                })
                .finally(() => {
                    this.fetching = false;
                });
        },
    },
};
